<script setup lang="ts">
import { computed } from "vue";
import dayjs from "dayjs";
import { convertImageUrl } from "@/utils/imageUrl";
import defaultPosterImg from "@/assets/images/default-poster.png";

const props = defineProps<{
  item: any;
  // 该作品的评价数据（评分/Votes/みたい/みた/星别分布），未取到时为空
  comment?: any;
}>();

// 转换图片 URL，将 HTTP 转换为通过代理访问的 HTTPS；没有封面时使用默认占位图
const posterUrl = computed(() => convertImageUrl(props.item.poster) || defaultPosterImg);

// 封面链接打不开（404/网络错误等）时兜底替换为默认占位图，避免出现裂图
const resolvedDefaultPosterImg = new URL(defaultPosterImg, window.location.href).href;
const onPosterError = (event: Event) => {
  const imgEl = event.target as HTMLImageElement;
  if (imgEl.src === resolvedDefaultPosterImg) return;
  imgEl.src = defaultPosterImg;
};

// 空值统一显示为「—」，不显示错误的替代数据
const EMPTY_TEXT = "—";

// 配給会社：后端按 distributors.seq 升序返回，多个时用「・」连接；无数据时显示「—」
const distributorText = computed(() => {
  const names = props.item.distributorNames;
  if (!Array.isArray(names) || names.length === 0) return EMPTY_TEXT;
  const valid = names.filter((name: string) => !!name);
  return valid.length > 0 ? valid.join("・") : EMPTY_TEXT;
});

// 制作国
const countryText = computed(() => {
  const names = props.item.countryJpNames;
  if (!Array.isArray(names) || names.length === 0) return EMPTY_TEXT;
  return names.join("・");
});

const headCinemaText = computed(() => props.item.headCinema || EMPTY_TEXT);

const openDateText = computed(() => (props.item.openDate
  ? dayjs(props.item.openDate).format("YYYY / MM / DD")
  : EMPTY_TEXT));

const durationText = computed(() => `${props.item.duration ? props.item.duration : 0}分`);

// 千以上缩写为 K，与原展示口径保持一致
const getRatingCount = (rating: number) => {
  if (!rating) return 0;
  if (rating <= 1000) return rating;
  return `${(rating / 1000).toFixed(1)}K`;
};

const rating = computed(() => props.comment?.rating || 0);

// 星级填充宽度（支持任意小数，替代原先依赖外链雪碧图的实现）
const starFillPercent = computed(() => Math.min(Math.max(rating.value, 0), 5) * 20);

// 各星别评价占比：分母为 1〜5 星评价数之和
const rateTotal = computed(() => {
  const c = props.comment;
  if (!c) return 0;
  return (c.rate1Count || 0) + (c.rate2Count || 0) + (c.rate3Count || 0)
    + (c.rate4Count || 0) + (c.rate5Count || 0);
});

const getRatePercent = (star: number) => {
  if (!rateTotal.value) return 0;
  return ((props.comment?.[`rate${star}Count`] || 0) / rateTotal.value) * 100;
};
</script>

<template>
  <article class="movie-item">
    <!-- 左：海报 + 基础信息 -->
    <div class="movie-main">
      <div class="poster-box">
        <img class="poster" :src="posterUrl" :alt="props.item.title" @error="onPosterError" />
      </div>
      <div class="movie-info">
        <h3 class="movie-name">{{ props.item.title }}</h3>
        <p v-if="props.item.englishTitle" class="movie-sub-name">{{ props.item.englishTitle }}</p>
        <dl class="info-list">
          <!-- 配給会社：位置固定在「制作国」正上方 -->
          <div class="info-row">
            <dt>配給</dt>
            <dd>{{ distributorText }}</dd>
          </div>
          <div class="info-row">
            <dt>制作国</dt>
            <dd>{{ countryText }}</dd>
          </div>
          <div class="info-row">
            <dt>時間</dt>
            <dd>{{ durationText }}</dd>
          </div>
          <div class="info-row">
            <dt>ヘッド館</dt>
            <dd>{{ headCinemaText }}</dd>
          </div>
          <div class="info-row">
            <dt>公開日</dt>
            <dd>{{ openDateText }}</dd>
          </div>
        </dl>
      </div>
    </div>

    <!-- 右：评价数据 -->
    <div class="movie-stats">
      <!-- 星别评价分布 -->
      <div class="rate-dist">
        <div v-for="star in [5, 4, 3, 2, 1]" :key="star" class="dist-row">
          <span class="dist-star">{{ star }}</span>
          <span class="dist-track">
            <span class="dist-bar" :style="{ width: `${getRatePercent(star)}%` }" />
          </span>
          <span class="dist-percent">{{ getRatePercent(star).toFixed(1) }}%</span>
        </div>
      </div>

      <!-- 评分 / Votes / みたい / みた -->
      <div class="rate-summary">
        <div class="score-line">
          <span class="score">{{ rating.toFixed(1) }}</span>
          <span class="stars" :title="`${rating.toFixed(1)} / 5`">
            <span class="stars-base">
              <i v-for="n in 5" :key="`b${n}`" class="fas fa-star" />
            </span>
            <span class="stars-fill" :style="{ width: `${starFillPercent}%` }">
              <i v-for="n in 5" :key="`f${n}`" class="fas fa-star" />
            </span>
          </span>
        </div>
        <p class="votes">{{ getRatingCount(props.comment?.rateCount) }} Votes</p>
        <div class="counter-group">
          <div class="counter">
            <span class="counter-label">
              <img src="@/assets/images/like.svg" alt="" />みたい！
            </span>
            <span class="counter-value">{{ getRatingCount(props.comment?.wantToSee) }}</span>
          </div>
          <div class="counter">
            <span class="counter-label">
              <img src="@/assets/images/saw.svg" alt="" />みた！
            </span>
            <span class="counter-value">{{ getRatingCount(props.comment?.haveSeen) }}</span>
          </div>
        </div>
      </div>
    </div>
  </article>
</template>

<style lang="less" scoped>
.movie-item {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 24px;
  padding: 16px 20px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius, 6px);
  background: var(--bg-panel);
  box-shadow: var(--shadow-sm);
  transition: box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    border-color: color-mix(in srgb, var(--brand-color) 45%, var(--border-color));
    box-shadow: var(--shadow-md);
  }

  p,
  dl,
  dd,
  dt,
  h3 {
    margin: 0;
    padding: 0;
  }
}

/* 左侧：海报 + 基础信息 */
.movie-main {
  display: flex;
  gap: 16px;
  min-width: 0;
  flex: 1 1 auto;
}

.poster-box {
  flex-shrink: 0;
  width: 97px;
  height: 135px;
  overflow: hidden;
  border-radius: 4px;
  background: color-mix(in srgb, var(--text-primary) 6%, var(--bg-panel));
}

.poster {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.movie-info {
  min-width: 0;
  flex: 1 1 auto;
}

.movie-name {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.35;
  color: var(--text-primary);
  word-break: break-word;
}

.movie-sub-name {
  margin-top: 2px !important;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
  color: var(--text-tertiary, var(--text-secondary));
  word-break: break-word;
}

.info-list {
  margin-top: 12px !important;
  display: grid;
  gap: 4px;
}

.info-row {
  display: flex;
  gap: 8px;
  font-size: 13px;
  line-height: 1.5;

  dt {
    flex-shrink: 0;
    min-width: 62px;
    color: var(--text-tertiary, var(--text-secondary));
    font-weight: 500;
  }

  dd {
    min-width: 0;
    color: var(--text-primary);
    font-weight: 600;
    word-break: break-word;
  }
}

/* 右侧：评价数据 */
.movie-stats {
  display: flex;
  align-items: flex-start;
  gap: 28px;
  flex-shrink: 0;
}

.rate-dist {
  display: grid;
  gap: 8px;
  padding-top: 2px;
}

.dist-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--text-secondary);
}

.dist-star {
  width: 10px;
  text-align: right;
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
}

.dist-track {
  position: relative;
  display: block;
  width: 120px;
  height: 6px;
  flex-shrink: 0;
  border-radius: 999px;
  background: color-mix(in srgb, var(--text-primary) 12%, transparent);
  overflow: hidden;
}

.dist-bar {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: var(--brand-color);
  transition: width 0.3s ease;
}

.dist-percent {
  width: 46px;
  flex-shrink: 0;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.rate-summary {
  min-width: 132px;
}

.score-line {
  display: flex;
  align-items: center;
  gap: 8px;
}

.score {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.1;
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
}

/* 星级：底层灰星 + 上层按评分裁切的实心星，替代原外链雪碧图 */
.stars {
  position: relative;
  display: inline-block;
  white-space: nowrap;
  line-height: 1;
  font-size: 13px;

  .stars-base {
    color: color-mix(in srgb, var(--text-primary) 20%, transparent);
  }

  .stars-fill {
    position: absolute;
    top: 0;
    left: 0;
    overflow: hidden;
    color: var(--brand-color);
  }

  i {
    margin-right: 1px;
  }
}

.votes {
  margin-top: 4px !important;
  font-size: 13px;
  color: var(--text-tertiary, var(--text-secondary));
  font-variant-numeric: tabular-nums;
}

.counter-group {
  margin-top: 12px;
  display: grid;
  gap: 8px;
}

.counter {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.counter-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 700;
  color: var(--text-secondary);

  img {
    width: 14px;
    height: 14px;
  }
}

.counter-value {
  font-size: 16px;
  font-weight: 800;
  color: var(--brand-color);
  font-variant-numeric: tabular-nums;
}

/* 平板：评价数据换到下一行，避免信息被挤压 */
@media (max-width: 1100px) {
  .movie-item {
    flex-direction: column;
    gap: 16px;
  }

  .movie-stats {
    gap: 24px;
  }

  .dist-track {
    width: 160px;
  }
}

@media (max-width: 768px) {
  .movie-item {
    padding: 14px;
    gap: 14px;
  }

  .movie-main {
    gap: 12px;
  }

  .poster-box {
    width: 82px;
    height: 114px;
  }

  .movie-name {
    font-size: 17px;
  }

  .info-row {
    font-size: 12px;

    dt {
      min-width: 54px;
    }
  }

  .movie-stats {
    flex-direction: column;
    gap: 16px;
    padding-top: 14px;
    border-top: 1px dashed var(--border-color);
  }

  .rate-dist {
    width: 100%;
  }

  .dist-row {
    gap: 10px;
  }

  /* 移动端分布条占满剩余宽度 */
  .dist-track {
    width: auto;
    flex: 1 1 auto;
  }

  .rate-summary {
    width: 100%;
  }

  .counter-group {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .counter {
    justify-content: flex-start;
    gap: 8px;
  }
}
</style>
